import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Package, MapPin, Bell, Truck, CheckCircle2, Clock3, Sparkles } from 'lucide-react';
import { Header } from '@/components/Header';

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F9FC] via-white to-[#F0F8FF]">
      <Header />

      {/* Hero Section - Rediseñado */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Animated background gradients */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(1200px circle at 20% 15%, rgba(105,199,234,0.4), transparent 60%), radial-gradient(900px circle at 80% 10%, rgba(0,153,64,0.15), transparent 55%)',
          }}
        ></div>
        
        {/* Subtle animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#69C7EA] rounded-full opacity-40 animate-pulse"></div>
          <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-[#009940] rounded-full opacity-30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-1/3 left-1/2 w-2 h-2 bg-[#374B67] rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          {/* Logo Ayuntamiento - PROTAGONISTA */}
          <div className="flex flex-col items-center justify-center mb-10">
            <div className="flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_majadahonda-gis/artifacts/i574mq3v_image.png"
                alt="Ayuntamiento de Majadahonda"
                data-testid="home-majadahonda-logo"
                className="h-32 sm:h-40 md:h-48 w-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
                style={{ filter: 'drop-shadow(0 12px 32px rgba(55,75,103,0.25))' }}
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm sm:text-base font-semibold text-[#374B67] tracking-wide uppercase">
                Ayuntamiento de Majadahonda
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Concejalía de Medio Ambiente · Servicio Municipal
              </p>
            </div>
          </div>

          {/* Hero Content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#009940]/10 to-[#69C7EA]/10 text-[#374B67] text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-6 border border-[#69C7EA]/20 backdrop-blur-sm">
              <Sparkles size={14} className="text-[#009940]" />
              Servicio Municipal de Recogida de Enseres
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#374B67] leading-tight mb-4">
              Solicitud de Recogida
              <span className="block mt-2 bg-gradient-to-r from-[#69C7EA] to-[#009940] bg-clip-text text-transparent">
                de Enseres
              </span>
            </h1>
            
            <p className="text-slate-600 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
              Sistema municipal para la gestión de recogida de muebles y enseres en Majadahonda.
              <span className="block mt-2 font-medium text-[#374B67]">Fácil, rápido y con seguimiento en tiempo real.</span>
            </p>

            {/* Main cards - REDISEÑADAS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Nueva solicitud - Card premium */}
              <div
                data-testid="home-new-request-card"
                className="group relative bg-gradient-to-br from-white to-[#009940]/5 rounded-3xl shadow-lg border-2 border-[#009940]/20 p-8 text-left hover:shadow-2xl hover:border-[#009940]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#009940]/0 to-[#009940]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#009940] to-[#007F35] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Package size={28} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#374B67] mb-2 group-hover:text-[#009940] transition-colors">
                    Nueva Solicitud
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Registra una nueva solicitud de recogida de enseres en 5 sencillos pasos.
                  </p>
                  <Link
                    to="/solicitud"
                    data-testid="home-new-request-button"
                    className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#009940] to-[#007F35] hover:from-[#007F35] hover:to-[#006B2D] text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl group-hover:gap-3"
                  >
                    Crear Solicitud 
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Consultar estado - Card premium */}
              <div
                data-testid="home-status-lookup-card"
                className="group relative bg-gradient-to-br from-white to-[#69C7EA]/5 rounded-3xl shadow-lg border-2 border-[#69C7EA]/20 p-8 text-left hover:shadow-2xl hover:border-[#69C7EA]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#69C7EA]/0 to-[#69C7EA]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#69C7EA] to-[#5AB5D8] flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Search size={28} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#374B67] mb-2 group-hover:text-[#69C7EA] transition-colors">
                    Consultar Estado
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Consulta el estado de tu solicitud usando tu código de ticket.
                  </p>
                  <Link
                    to="/consulta"
                    data-testid="home-status-button"
                    className="flex items-center justify-center gap-2 w-full bg-white border-2 border-[#374B67]/30 hover:bg-[#374B67] hover:border-[#374B67] text-[#374B67] hover:text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg group-hover:gap-3"
                  >
                    Consultar Estado 
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - MEJORADO */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#374B67] mb-3">¿Cómo funciona?</h2>
            <p className="text-slate-600 text-lg">Proceso sencillo en 4 pasos</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: Package, title: '1. Solicita', desc: 'Rellena el formulario online', color: 'from-[#009940] to-[#007F35]' },
              { icon: Bell, title: '2. Confirmación', desc: 'Recibes email y WhatsApp', color: 'from-[#69C7EA] to-[#5AB5D8]' },
              { icon: Truck, title: '3. Recogida', desc: 'Planificamos la ruta', color: 'from-[#374B67] to-[#2A3B52]' },
              { icon: CheckCircle2, title: '4. Completado', desc: 'Notificación final', color: 'from-[#10B981] to-[#059669]' }
            ].map((step, idx) => (
              <div key={idx} className="group text-center">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}>
                  <step.icon size={32} className="text-white" />
                </div>
                <h3 className="font-bold text-[#374B67] mb-2 text-base sm:text-lg">{step.title}</h3>
                <p className="text-sm text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features - NUEVO */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: MapPin, 
                title: 'Seguimiento GPS', 
                desc: 'Conoce la ubicación del vehículo en tiempo real',
                color: 'bg-[#69C7EA]/10',
                iconColor: 'text-[#69C7EA]'
              },
              { 
                icon: Clock3, 
                title: 'Respuesta Rápida', 
                desc: 'Confirmación inmediata y planificación eficiente',
                color: 'bg-[#009940]/10',
                iconColor: 'text-[#009940]'
              },
              { 
                icon: Bell, 
                title: 'Notificaciones', 
                desc: 'Mantente informado por email y WhatsApp',
                color: 'bg-[#374B67]/10',
                iconColor: 'text-[#374B67]'
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300">
                <div className={`w-14 h-14 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon size={26} className={feature.iconColor} />
                </div>
                <h3 className="font-bold text-[#374B67] mb-2 text-lg">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer - MEJORADO */}
      <footer className="bg-gradient-to-br from-[#374B67] to-[#2A3B52] text-white py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="font-bold mb-3 text-[#69C7EA]">Ayuntamiento de Majadahonda</h4>
              <p className="text-sm text-slate-300 leading-relaxed">
                Servicio municipal de recogida de enseres y muebles voluminosos.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-[#69C7EA]">Enlaces</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/solicitud" className="hover:text-white transition-colors">Nueva Solicitud</Link></li>
                <li><Link to="/consulta" className="hover:text-white transition-colors">Consultar Estado</Link></li>
                <li><Link to="/admin/login" className="hover:text-white transition-colors">Acceso Admin</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-[#69C7EA]">Gestión</h4>
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_majadahonda-gis/artifacts/0jkm610n_VALORIZA_color_negativo%20%282%29.svg" 
                  alt="Valoriza"
                  className="h-8 w-auto opacity-90"
                />
              </div>
              <p className="text-xs text-slate-400">
                Servicio gestionado por Valoriza Servicios Medioambientales
              </p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} Ayuntamiento de Majadahonda. Todos los derechos reservados.</p>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs">
              <Link to="/privacidad" className="hover:text-white transition-colors">Política de Privacidad (RGPD)</Link>
              <span className="opacity-30">·</span>
              <Link to="/consulta" className="hover:text-white transition-colors">Consultar solicitud</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
